import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Comments,
    CustomDelByNote,
    CustomDelete,
    Loader,
    NoItemsYet,
    NonBillable,
    Notes,
    SessionTimer,
    SimpleModal,
    SimpleTabs,
    SimpleTooltip,
    Warning,
} from 'components';
import { billStatuses, DRIVE, getDisplayFromFullType, isUnassignedAppt, scheduleStatuses, SERVICE, STAFF, UNAVAILABLE } from '../../../constants';
import { Details } from './details';
import {
    FindErrorItem,
    FindLoad,
    FindSuccessItem,
    Images,
    PermissionList,
    RolePermission,
    useModal,
    useSecondaryModal,
    apptIssueType,
    OrgIntegrations,
    sessionStatusEnums,
    formatSecondDuration,
    User, commentModels,
} from 'utils';
import { apptDetailList, apptDetailStatus, noHistoryYet, reasonEnumsConvert, serviceApptDetailList } from './constants';

import {
    appointmentActions,
    authActions,
    clientActions,
    httpRequestsOnErrorsActions,
    httpRequestsOnSuccessActions, noteActions,
    systemActions,
} from 'store';
import { apptEnums, renderSessionStatus } from '../../constants';
import { HistoryTab } from '../../../../history';
import { SignatureUploader } from '../../common/signatureUploader';
import { CancelAppt } from '../cancelAppt';
import { ApptFiles } from './apptFiles';
import { ApptErrorTab } from './apptErrorTab';
import { ApptStateInfo } from './apptStateInfo';
import { CompleteAppt } from './completeAppt';
import { RequireInfoBox } from './requireInfoBox';
import { ApptSessionNotes } from './apptSessionNotes';
import { ApptNotes } from './apptNotes';
import { ScheduleLinks } from './scheduleLinks';
import { ApptActionsWithModals } from '../../apptActionsWithModals';
import { CreateSession } from '../../../../clinicalFragments';
import { ScheduleDataCollection } from '../scheduleDataCollection';
import { useFutureApptWarning } from './useFutureApptWarning';
import moment from 'moment/moment';

const RENDER_COMPLETE_ACTION_TYPE = 'SET_APPOINTMENT_STATUS_COMPLETE';
const REMOVE_APPT_FROM_BILL = 'REMOVE_APPT_FROM_BILL';
const REMOVE_APPT_FROM_INVOICE = 'REMOVE_APPT_FROM_INVOICE';
const ACTION_TYPE = 'GET_APPOINTMENT_BY_ID';
const UN_RENDER_ACTION = 'UN_RENDER_APPT';
const UN_CANCEL_ACTION = 'UN_CANCEL_APPT';
const SET_APPOINTMENT_STATUS = 'SET_APPOINTMENT_STATUS';

export const ScheduleDetails = ({ currentId, getAfterSuccess, defaultTab }) => {
    const { item } = useSelector((state) => ({
        item: state.appointment.appointmentById,
    }));
    const apptType = getDisplayFromFullType(item?.type);
    const _isServiceAppmt = item?.type === SERVICE;
    const { userInfo } = User();
    const checkCancel = item?.locked
        ? RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) &&
          RolePermission([PermissionList.APPT_CANCEL?.code, PermissionList.APPT_SELF_CANCEL?.code])
        : RolePermission([PermissionList.APPT_SELF_CANCEL?.code, PermissionList.APPT_CANCEL?.code]);

    const checkUnCancel = item?.locked
        ? RolePermission([PermissionList.LOCKED_APPT_EDIT?.code]) &&
          RolePermission([PermissionList.APPT_UN_CANCEL?.code, PermissionList.APPT_SELF_UN_CANCEL?.code])
        : RolePermission([PermissionList.APPT_SELF_UN_CANCEL?.code, PermissionList.APPT_UN_CANCEL?.code]);
    const isOwnAppt = item?.staff?.id === userInfo?.id;
    const renderPermission = isOwnAppt
        ? (_isServiceAppmt
            ? [PermissionList.APPT_RENDER_COMPLETE?.code, PermissionList.APPT_RENDER_OWN_SERVICE?.code]
            : item?.type === STAFF
              ? [PermissionList.APPT_RENDER_COMPLETE?.code, PermissionList.APPT_COMPLETE_OWN_STAFF?.code]
              : item?.type === DRIVE
                ? [PermissionList.APPT_RENDER_COMPLETE?.code, PermissionList.APPT_COMPLETE_OWN_DRIVE?.code]
                : [PermissionList.APPT_RENDER_COMPLETE?.code])
        : [PermissionList.APPT_RENDER_COMPLETE?.code];
    const checkRenderComplete = item?.locked
        ? RolePermission([PermissionList.LOCKED_APPT_RENDER?.code]) && RolePermission(renderPermission)
        : RolePermission(renderPermission);
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(defaultTab ? defaultTab : 'Details');
    const { open, close } = useModal();
    const { openSecondary, closeSecondary } = useSecondaryModal();
    const loader = FindLoad(ACTION_TYPE);
    const detailList = _isServiceAppmt ? serviceApptDetailList(item) : apptDetailList(item);
    const backError = FindErrorItem(SET_APPOINTMENT_STATUS);
    const success = FindSuccessItem(SET_APPOINTMENT_STATUS);
    const unCancelError = FindErrorItem(UN_CANCEL_ACTION);
    const isNonBillable = _isServiceAppmt && item?.authorizedService?.billable === false;
    const canSkipTbDoj = RolePermission([PermissionList.APPT_RENDER_WITHOUT_TB_DOJ?.code]);
    const hasTbDojError = _isServiceAppmt && !canSkipTbDoj && item?.issues?.find((i) => i.issueType === apptIssueType.TB_DOJ_ERROR);
    const isFutureAppt = _isServiceAppmt && item?.startDate && moment.utc(item.startDate).startOf('day').isAfter(moment.utc().startOf('day'));
    const canManageFutureAppt = RolePermission([PermissionList.APPT_MANAGE_FUTURE?.code]);
    const blockFutureAppt = isFutureAppt && !canManageFutureAppt;
    // No staff yet: nothing to render, sign as provider or run a session for until staff is assigned.
    const unassigned = isUnassignedAppt(item);
    const blockRender = unassigned || hasTbDojError || isNonBillable || blockFutureAppt;
    const blockSessionStart = !item?.csessionStatus && (unassigned || blockFutureAppt);
    const [openComplete, setOpenComplete] = useState(false);
    const checkUnRenderPermissions = item?.locked
        ? RolePermission([PermissionList.APPT_UNRENDER?.code]) && RolePermission([PermissionList.LOCKED_APPT_RENDER?.code])
        : RolePermission([PermissionList.APPT_UNRENDER?.code]);
    const { cSession } = OrgIntegrations();

    useEffect(() => {
        if (success) {
            close();
            closeSecondary();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(SET_APPOINTMENT_STATUS));
        }
    }, [success]);

    useEffect(() => {
        return () => appointmentActions.removeAppointmentInfo();
    }, []);

    useEffect(() => {
        if (backError?.error) {
            close();
        }
        if (unCancelError?.error) {
            close();
            dispatch(httpRequestsOnErrorsActions.removeError(UN_CANCEL_ACTION));
        }
    }, [backError, unCancelError]);

    const afterSuccess = () => {
        if (getAfterSuccess) {
            getAfterSuccess();
        }
    };

    useEffect(() => {
        if (item?.client) {
            if (RolePermission([PermissionList.CLIENT_CONTACT_READ?.code])) {
                dispatch(clientActions.getClientsContacts(item?.client?.id));
            } else {
                dispatch(clientActions.getClientsContactInfo(item?.client?.id));
            }
        }
    }, [item]);

    useEffect(() => {
        dispatch(authActions.getMyProfile());
        dispatch(appointmentActions.getAppointmentById(currentId));
        dispatch(systemActions.getPlaces());
        dispatch(systemActions.getTelehealthStatus());
        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(SET_APPOINTMENT_STATUS));
        };
    }, []);

    const apptDetailTabsLabels = [
        { label: 'Details' },
        item?.type !== UNAVAILABLE && { label: 'Links' },
        cSession && item?.cSession?.id && item?.type !== UNAVAILABLE && { label: 'Data Collection' },
        item?.type !== UNAVAILABLE && { label: 'Session Notes' },
        { label: 'Comments' },
        item?.type !== UNAVAILABLE && { label: 'Notes' },
        RolePermission([PermissionList.APPT_FILE_READ?.code, PermissionList.APPT_FILE_MANAGE?.code]) &&
            item?.type !== UNAVAILABLE && { label: 'Files' },
        RolePermission([PermissionList.APPT_HISTORY_READ?.code]) && item?.type !== UNAVAILABLE && { label: 'History' },
        item?.type !== UNAVAILABLE && { label: 'Errors' },
    ];

    const apptTabContent = [
        {
            tabComponent: <Details item={item} currentId={currentId} />,
            tab: 'Details',
        },
        item?.type !== UNAVAILABLE && {
            tabComponent: <ScheduleLinks item={item} />,
            tab: 'Links',
        },
        cSession &&
            item?.cSession?.id &&
            item?.type !== UNAVAILABLE && {
                tabComponent: <ScheduleDataCollection appt={item} />,
                tab: 'Data Collection',
            },
        item?.type !== UNAVAILABLE && {
            tabComponent: <ApptSessionNotes item={item} />,
            tab: 'Session Notes',
        },

        {
            tabComponent: (
                <Comments
                    fromDetail={true}
                    resource={commentModels.Appt}
                    resourceId={item?.id}
                    label={'Appt'}
                    width={700}
                    height={600}
                    attachmentPurpose={'appt'}
                    enableAiImprove
                />
            ),
            tab: 'Comments',
        },

        item?.type !== UNAVAILABLE && {
            tabComponent: <ApptNotes item={item} />,
            tab: 'Notes',
        },
        RolePermission([PermissionList.APPT_FILE_READ?.code, PermissionList.APPT_FILE_MANAGE?.code]) &&
            item?.type !== UNAVAILABLE && {
                tabComponent: <ApptFiles item={item} />,
                tab: 'Files',
            },
        RolePermission([PermissionList.APPT_HISTORY_READ?.code]) &&
            item?.type !== UNAVAILABLE && {
                tabComponent: (
                    <HistoryTab
                        notYet={'notShow'}
                        currentId={item?.id}
                        onModel={'Appt'}
                        height={'medium'}
                        noHistoryYet={noHistoryYet}
                        noPagination={true}
                        saveParams={true}
                    />
                ),
                tab: 'History',
            },
        item?.type !== UNAVAILABLE && {
            tabComponent: <ApptErrorTab issues={item?.issues} />,
            tab: 'Errors',
        },
    ];

    const unRenderAppt = () => {
        const title = item?.type === SERVICE ? 'Un-render Appt' : 'Un-complete Appt';
        const text =
            item?.type === SERVICE
                ? 'Are you sure you want to un-render this appointment? Billing or processed timesheets will not be affected. Active timesheets will be deleted.'
                : 'Are you sure you want to un-complete this appointment? Billing or processed timesheets will not be affected. Active timesheets will be deleted.';
        const innerText = item?.type === SERVICE ? 'Un-Render' : 'Un-Complete';
        open(
            <CustomDelete
                info={title}
                text={text}
                innerText={innerText}
                color={'blue'}
                handleDel={() => dispatch(appointmentActions.unRenderAppt(item?.id))}
                handleClose={() => close()}
                actionType={UN_RENDER_ACTION}
                afterSuccess={afterSuccess}
            />
        );
    };

    const unCancelAppt = () => {
        open(
            <CustomDelete
                info={'Un-cancel this appointment?'}
                text={'This will restore the appt and make it active again. Any linked notes, sign, or billing data will be preserved.'}
                innerText={'Un-cancel appt'}
                innerIcon={Images.rotate}
                color={'blue'}
                handleDel={() => dispatch(appointmentActions.unCancelAppt(item?.id))}
                actionType={UN_CANCEL_ACTION}
                handleClose={() => close()}
                afterSuccess={afterSuccess}
                body={
                    <div className="deleting-client-info-box">
                        <img src={Images.eventCalendar} alt="icon" />
                        <p style={{ fontWeight: 400 }}>{`Appt: ${item?.displayId}`}</p>
                    </div>
                }
            />
        );
    };

    const removeFromBill = () => {
        const isInvoice = item?.canRemoveFromInvoice;
        open(
            <CustomDelByNote
                info={isInvoice ? 'Remove Appointment from Invoice' : 'Remove Appointment from Claim'}
                text={
                    isInvoice
                        ? 'Are you sure you want to remove this appointment from invoice? The appointment will be set back to Pending.'
                        : 'Are you sure you want to remove this appointment from the claim?'
                }
                innerText={'Remove'}
                inputLabel={'Reason for removing*'}
                handleDel={(comment) =>
                    isInvoice
                        ? dispatch(appointmentActions.removeApptFromInvoice(item?.id, { reason: comment }))
                        : dispatch(appointmentActions.removeApptFromBill(item?.id, { apptIds: [item?.id], reason: comment }))
                }
                actionType={isInvoice ? REMOVE_APPT_FROM_INVOICE : REMOVE_APPT_FROM_BILL}
                successType={isInvoice ? REMOVE_APPT_FROM_INVOICE : REMOVE_APPT_FROM_BILL}
                afterSuccess={afterSuccess}
            />
        );
    };

    const checkFutureDateAndRun = useFutureApptWarning(item);

    function handleStatusChange(type, eventType) {
        checkFutureDateAndRun(() => {
            if (eventType === SERVICE) {
                setOpenComplete(true);
            } else {
                open(
                    <CustomDelete
                        text={`Are you sure you want to complete this appointment?`}
                        info={`Complete This Appointment?`}
                        innerText={`Complete Appointment`}
                        handleDel={() => dispatch(appointmentActions.setAppointmentStatus(item?.id, type, ''))}
                        handleClose={() => close()}
                        actionType={RENDER_COMPLETE_ACTION_TYPE}
                        color={'blue'}
                        afterSuccess={afterSuccess}
                    />
                );
            }
        });
    }

    const getSessionNotes = () => {
        if(activeTab === 'Session Notes'){
            dispatch(noteActions.getGlobalNotes(item.id, 'Appt', { limit: 100 }, ));
        }
    }

    const openSignature = () => {
        checkFutureDateAndRun(() => {
            open(<SignatureUploader appt={item} afterSuccess={getSessionNotes} />);
        });
    };

    const editReason = () => {
        open(<CancelAppt afterSuccess={afterSuccess} edit={true} />);
    };

    const openSessionModal = () => {
        if (cSession && item?.id && item?.cSession?.id) {
            window.open(`/appointment/${item.id}/session/${item.cSession.id}`, '_blank');
        }

        // openSecondary(<Session />, {
        //     styles: {
        //         bodyStyle: {
        //             width: '100vw',
        //             maxHeight: '100vh',
        //             height: '100vh',
        //             padding: '0 0 200px 0',
        //             background: '#F5F5F5',
        //         },
        //         wrapperStyle: { height: '100vh', borderRadius: 0, padding: 0 },
        //         headerStyle: { display: 'none' },
        //     },
        // });
    };

    const handleSession = () => {
        if (item?.cSession?.id) {
            openSessionModal();
            closeSecondary();
        } else {
            checkFutureDateAndRun(() => {
                dispatch(clientActions.getClientsById(item?.client?.id));
                openSecondary(
                    <CreateSession
                        afterSuccess={() => {
                            closeSecondary();
                            // openSessionModal();
                        }}
                    />
                );
            });
        }
    };

    if (loader?.length) {
        return (
            <div className="appt-detail-modal">
                <Loader />
            </div>
        );
    }

    return (
        <div className="appt-detail-modal">
            {item?.id ? (
                <div className="appt-detail-wrapper">
                    <div>
                        <div>
                            <div className="flex-align-center" style={{ gap: 8 }}>
                                <p className="appt-detail-title">{apptType} ID:</p>
                                <p className="appt-detail-title" style={{ fontWeight: 400 }}>
                                    {item?.displayId}
                                </p>
                                <div>{apptDetailStatus(item?.status, item, false, editReason)}</div>
                                <ApptStateInfo refresh={true} appt={item} />
                                {_isServiceAppmt && item?.authorizedService?.billable === false && (
                                    <NonBillable noText={true} toolText="Non-billable authorization" />
                                )}
                                <div>{renderSessionStatus(item?.csessionStatus)}</div>
                            </div>
                            {item?.cancellation?._id && (
                                <div className="canceled-reason-wrapper" style={{ marginBottom: '-15px' }}>
                                    <div className="canceled-reason">
                                        {item?.cancellation?.reason ? (
                                            <SimpleTooltip title={<p>{item?.cancellation?.reason}</p>} placement="top" arrow>
                                                <p>
                                                    Reason:{' '}
                                                    {item?.cancellation?.reason?.length > 40
                                                        ? `${item?.cancellation?.reason.slice(0, 40)}...`
                                                        : item?.cancellation?.reason}
                                                </p>
                                            </SimpleTooltip>
                                        ) : (
                                            <p>
                                                {`Cancellation Reason: ${
                                                    item?.cancellation?.type ? reasonEnumsConvert[item?.cancellation?.type] : '--'
                                                }`}
                                            </p>
                                        )}
                                        <button onClick={editReason} className="reset-btn-no-paddings">
                                            <img
                                                src={Images.noteEdit}
                                                alt="icon"
                                                style={{
                                                    filter: 'invert(24%) sepia(96%) saturate(3084%) hue-rotate(209deg) brightness(95%) contrast(91%)',
                                                }}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="space-between-flex-start" style={{ margin: '16px 0 12px' }}>
                                <div className="full-width">
                                    {detailList?.map(
                                        (i, j) =>
                                            i?.value && (
                                                <div key={j} className="appt-detail-box">
                                                    <p className="appt-detail-modal-title">{i?.title}</p>
                                                    <p className={i?.link ? 'appt-detail-modal-link-value' : 'appt-detail-modal-value'}>
                                                        {i?.value}
                                                    </p>
                                                </div>
                                            )
                                    )}
                                </div>
                                <div className="flex-column-end">
                                    <div className="flex-align-center" style={{ gap: 12 }}>
                                        {item?.type === SERVICE && (
                                            <div className="flex-align-center" style={{ gap: 12 }}>
                                                {/*<button className="new-action-btn-box" onClick={() => setActiveTab('Notes')}>*/}
                                                {/*    <img src={Images.manageNote} alt="icon" />*/}
                                                {/*    <p>Notes</p>*/}
                                                {/*</button>*/}

                                                {
                                                    // item?.status === scheduleStatuses.PENDING &&
                                                    (item?.status === scheduleStatuses.PENDING ||
                                                        item?.status === scheduleStatuses.COMPLETED) && (
                                                        <SimpleTooltip
                                                            disableHoverListener={!blockFutureAppt}
                                                            disableTouchListener={!blockFutureAppt}
                                                            title={<p>Cannot add signatures to future appointments.</p>}
                                                            placement="top"
                                                        >
                                                            <button
                                                                className="new-action-btn-box"
                                                                onClick={openSignature}
                                                                disabled={!!blockFutureAppt}
                                                                style={{ opacity: blockFutureAppt ? 0.5 : 1 }}
                                                            >
                                                                <img
                                                                    style={{
                                                                        filter: 'brightness(0) saturate(100%) invert(31%) sepia(10%) saturate(1368%) hue-rotate(177deg) brightness(89%) contrast(80%)',
                                                                    }}
                                                                    src={Images.plus}
                                                                    alt="icon"
                                                                />
                                                                <p>Add Sign.</p>
                                                            </button>
                                                        </SimpleTooltip>
                                                    )
                                                }
                                            </div>
                                        )}
                                        <ApptActionsWithModals item={item} afterSuccess={afterSuccess} />
                                    </div>
                                    {item?.type === SERVICE && <RequireInfoBox />}
                                </div>
                            </div>
                        </div>

                        <SimpleTabs
                            getTab={(e) => setActiveTab(e)}
                            tab={activeTab}
                            defaultTab={'Details'}
                            tabsLabels={apptDetailTabsLabels}
                            tabsContent={apptTabContent}
                            customTabPanel={'customTabPanel'}
                        />
                    </div>

                    {item?.type !== UNAVAILABLE && (
                        <div className="flex-end" style={{ gap: 32, alignItems: 'flex-start', paddingBottom: 12 }}>
                            {backError?.error && (
                                <Warning
                                    text={backError?.error}
                                    textStyles={{ fontWeight: 600 }}
                                    styles={{ alignItems: 'center' }}
                                    boxStyle={{ margin: 0 }}
                                />
                            )}
                            {item?.status === apptEnums.PENDING ? (
                                <div className="flex-align-center" style={{ gap: 24 }}>
                                    {checkCancel && item?.type !== UNAVAILABLE && (
                                        <button
                                            style={{ minWidth: 157, width: 157, height: 44 }}
                                            className="cancel-template-appts"
                                            onClick={() => open(<CancelAppt afterSuccess={afterSuccess} />)}
                                        >
                                            <img src={Images.xCircle} alt="cancel" />
                                            <p> Cancel Appt </p>
                                        </button>
                                    )}
                                    {checkRenderComplete && (
                                        <SimpleTooltip
                                            disableHoverListener={!blockRender}
                                            disableTouchListener={!blockRender}
                                            title={
                                                <p>
                                                    {unassigned
                                                        ? 'Assign a staff member to render this appointment.'
                                                        : blockFutureAppt
                                                        ? 'Future appointments cannot be rendered.'
                                                        : isNonBillable
                                                          ? 'Authorization is marked as non-billable. The appointment cannot be rendered.'
                                                          : 'Appointment contains issues and cannot be rendered.'}
                                                </p>
                                            }
                                            placement="top-end"
                                        >
                                            <button
                                                style={{ opacity: blockRender ? 0.5 : 1 }}
                                                className="render-btn-box"
                                                disabled={!!blockRender}
                                                onClick={() => handleStatusChange(_isServiceAppmt ? 'render' : 'complete', item?.type)}
                                            >
                                                {_isServiceAppmt ? 'Render Appt' : 'Complete Appt'}
                                            </button>
                                        </SimpleTooltip>
                                    )}

                                    {cSession && RolePermission([PermissionList.GOAL_READ?.code]) && _isServiceAppmt && (
                                        <SimpleTooltip
                                            disableHoverListener={!blockSessionStart}
                                            disableTouchListener={!blockSessionStart}
                                            title={
                                                <p>
                                                    {unassigned
                                                        ? 'Assign a staff member to start this session.'
                                                        : 'Cannot start a session on a future appointment.'}
                                                </p>
                                            }
                                            placement="top"
                                        >
                                            <button
                                                className="session-btn-box"
                                                onClick={handleSession}
                                                disabled={!!blockSessionStart}
                                                style={{ opacity: blockSessionStart ? 0.5 : 1 }}
                                            >
                                                {item?.csessionStatus && <img src={Images.arrowOrangeRight} alt="arrow" />}
                                                {item?.csessionStatus ? (
                                                    <div className="flex-align-center">
                                                        <p>View Session</p>
                                                        {item?.cSession?.status === sessionStatusEnums.IN_PROGRESS ? (
                                                            <p>
                                                                &nbsp; (<SessionTimer sessionById={item?.cSession} /> elapsed)
                                                            </p>
                                                        ) : (
                                                            item?.cSession?.totalDuration && (
                                                                <p>&nbsp; ({formatSecondDuration(item?.cSession?.totalDuration)})</p>
                                                            )
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p>Start Session</p>
                                                )}
                                            </button>
                                        </SimpleTooltip>
                                    )}
                                </div>
                            ) : (
                                <div className="flex-align-center" style={{ gap: 24 }}>
                                    {_isServiceAppmt && (
                                        <div>
                                            {
                                                // RolePermission([PermissionList.APPT_UNRENDER?.code]))
                                                checkUnRenderPermissions &&
                                                    item?.billStatus === billStatuses?.NOT_BILLED &&
                                                    item?.status !== apptEnums.CANCELLED && (
                                                        <button className="render-btn-box" onClick={unRenderAppt}>
                                                            {'Un-render Appt'}
                                                        </button>
                                                    )
                                            }

                                            {RolePermission([PermissionList.REMOVE_CLAIM_APPT?.code]) &&
                                                item?.billStatus === billStatuses?.BILLED &&
                                                item?.status !== apptEnums.CANCELLED &&
                                                item?.canRemoveFromInvoice !== false && (
                                                    <button className="render-btn-box" onClick={removeFromBill}>
                                                        {item?.canRemoveFromInvoice ? 'Remove from Invoice' : 'Remove from Claim'}
                                                    </button>
                                                )}
                                        </div>
                                    )}

                                    {!_isServiceAppmt &&
                                        RolePermission([PermissionList.APPT_UNRENDER?.code]) &&
                                        item?.status !== apptEnums.CANCELLED && (
                                            <button className="render-btn-box" onClick={unRenderAppt}>
                                                {'Un-complete Appt'}
                                            </button>
                                        )}

                                    {item?.status === apptEnums.CANCELLED && checkUnCancel && (
                                        <button className="render-btn-box flex-align-center" onClick={unCancelAppt} style={{ gap: 8 }}>
                                            <img src={Images.rotate} alt="refresh" />
                                            {'Un-cancel Appt'}
                                        </button>
                                    )}

                                    {item?.cSession?.id && (
                                        <button className="session-btn-box" onClick={handleSession}>
                                            <img src={Images.arrowOrangeRight} alt="arrow" />
                                            <div className="flex-align-center">
                                                <p>View Session</p>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <NoItemsYet
                    containerStyles={{ position: 'relative' }}
                    height={'80vh'}
                    text={'Appointment was not found'}
                    image={Images.noAppt}
                />
            )}

            <SimpleModal
                handleOpenClose={() => setOpenComplete(null)}
                openDefault={openComplete}
                content={
                    <div className="modal-wrapper-style">
                        <div className="modal-body-style">
                            <CompleteAppt handleClose={() => setOpenComplete(null)} />
                        </div>
                    </div>
                }
            />
        </div>
    );
};
